import { useClientRefetchOrder, useOnReserveOrCancelOrder } from '@common';
import { isDemoOrder, isStockOrder } from '@common/constants';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectOrder, selectVehicleAvailabilityCode } from '@store/selectors';
import {
  selectIsSelectedVehicleUnavailable,
  selectIsUnavailablePimData,
  selectIsVehicleValidForProceed,
  selectReserveButtonStage,
} from '@store/selectors/salesWizardSelector';
import { useState, useEffect, useRef } from 'react';
import { OtherDealerVehicleReserveOrSignValidationMsg } from '../../utils/sales/vehicleStatus';
import { formatReservationDate, ReserveButtonStages } from 'types';
import {
  isIFSReservationInProgress as checkIFSReservationInProgress,
  shouldShowReservationInfo as shouldShowReservationInfoByDetails,
} from '@utils/reservation/reservationUtils';
import { setOptimisticReserveFailModalStatus } from '@store/slices/salesWizardSlice';
import { NextOrderReserveStatus } from 'next-common';
export const getReservationDetailsState = (order, reserveButtonStage) => {
  const reservationToDate = order?.reserveDetails?.reservationToDate ?? '';
  const isReservationInWaiting = reserveButtonStage === ReserveButtonStages.WAITING;

  const showReservationInfo =
    !isReservationInWaiting && shouldShowReservationInfoByDetails(order?.reserveDetails, reservationToDate);

  return {
    reservationToDateLabel: formatReservationDate(reservationToDate),
    showReservationInfo,
  };
};

export const shouldShowReservationInfo = (order, reserveButtonStage): boolean => {
  return getReservationDetailsState(order, reserveButtonStage).showReservationInfo;
};

export const useReservationCartProps = () => {
  const { onReserveOrCancelOrder } = useOnReserveOrCancelOrder();

  const dispatch = useAppDispatch();
  const order = useAppSelector(selectOrder);
  const refetchOrder = useClientRefetchOrder({ orderId: order?.id });
  const reserveButtonStage = useAppSelector(selectReserveButtonStage);
  const isSelectedVehicleUnavailable = useAppSelector(selectIsSelectedVehicleUnavailable);
  const isUnavailablePimData = useAppSelector(selectIsUnavailablePimData);
  const isVehicleValidForReserveOrSign = useAppSelector(selectIsVehicleValidForProceed);
  const vehicleAvailabilityCode = useAppSelector(selectVehicleAvailabilityCode);
  const isIFSReservationInProgress = checkIFSReservationInProgress(order);
  const [showReserveButton, setShowReserveButton] = useState<boolean>(true);
  const refetchOrderRef = useRef(refetchOrder);

  useEffect(() => {
    refetchOrderRef.current = refetchOrder;
  }, [refetchOrder]);

  const effectiveVehicleUnavailable = Boolean(
    isSelectedVehicleUnavailable || isUnavailablePimData || !isVehicleValidForReserveOrSign
  );

  useEffect(() => {
    if (order?.isEditOrder) {
      setShowReserveButton(false);
    }
    if (isStockOrder(order?.lead?.orderType) || isDemoOrder(order?.lead?.orderType)) {
      setShowReserveButton(false);
    }

    if (order?.nextOrderReserveStatus === NextOrderReserveStatus.FAILURE) {
      dispatch(setOptimisticReserveFailModalStatus(true));
    }
  }, [order]);

  return {
    reserveButtonStage,
    showReserveButton: Boolean(showReserveButton) && !isUnavailablePimData,
    isSelectedVehicleUnavailable: effectiveVehicleUnavailable,
    onReserveVehicle: () => onReserveOrCancelOrder(order, reserveButtonStage),
    reservationInfoText: isVehicleValidForReserveOrSign
      ? ''
      : OtherDealerVehicleReserveOrSignValidationMsg(vehicleAvailabilityCode),
    ...getReservationDetailsState(order, reserveButtonStage),
    isIFSReservationInProgress,
  };
};
